import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly baseUrl = (environment as any).apiBaseUrl || '';

  constructor(private readonly http: HttpClient) {
  }



  login(payload: { email: string }): Observable<any> {
    const url = `${this.baseUrl}/api/auth/login`;

    return this.http.post(url, payload);
  }

  register(payload: any): Observable<any> {
    const url = `${this.baseUrl}/api/auth/register`;

    return this.http.post(url, payload);
  }

  verifyOtp(payload: { otp: string }, token?: string): Observable<any> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    const url = `${this.baseUrl}/api/auth/verify-otp`;

    return this.http.post(url, payload, { headers });
  }

  getProfile(): Observable<any> {
    const url = `${this.baseUrl}/api/auth/profile`;

    return this.http.get(url);
  }

  updateProfile(payload: any): Observable<any> {
    const url = `${this.baseUrl}/api/auth/profile`;
    return this.http.put(url, payload);
  }

  // Programs
  listPrograms(): Observable<any> {
    const url = `${this.baseUrl}/api/program/list`;

    return this.http.get(url);
  }

  getProgramDetail(id: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/${id}`;
    return this.http.get(url);
  }
  
  getProgram(id: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/form/${id}`;
    return this.http.get(url);
  }

  applyProgram(payload: any): Observable<any> {
    const url = `${this.baseUrl}/api/program/apply`;
    return this.http.post(url, payload);
  }

  submitStaticProgram(payload: any, endpoint?: string): Observable<any> {
    const url = endpoint 
      ? `${this.baseUrl}${endpoint}` 
      : `${this.baseUrl}/api/program/static-submit`;
    return this.http.post(url, payload);
  }

  getProgramSalesforceDetail(
    programId: string | number,
    applicationId?: string | number | null
  ): Observable<any> {
    const appParam =
      applicationId != null && String(applicationId).trim() !== ''
        ? `&application_id=${encodeURIComponent(String(applicationId))}`
        : '';
    const url = `${this.baseUrl}/api/program/program-salesforce-detail?id=${programId}${appParam}`;
    return this.http.get(url);
  }

  reuploadStaticProgram(payload: any): Observable<any> {
    const url = `${this.baseUrl}/api/program/staticform/reupload`;
    return this.http.put(url, payload);
  }

  // File endpoints
  getFile(path: string): Observable<Blob> {
    const url = `${this.baseUrl}/api/file/${path}`;
    return this.http.get(url, { responseType: 'blob' });
  }
 

  // Get file with full response including headers (for extracting filename)
  getFileWithHeaders(path: string): Observable<{ blob: Blob; filename: string }> {
    const url = `${this.baseUrl}/api/file/${path}`;
    return this.http.get(url, { 
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        // Extract filename from response headers
        const contentDisposition = response.headers.get('content-disposition') || '';
        const filenameHeader = response.headers.get('filename') || '';
        
        // Try to get filename from 'filename' header first, then from content-disposition
        let filename = filenameHeader;
        
        if (!filename && contentDisposition) {
          // Extract filename from content-disposition header (e.g., "attachment; filename=example.pdf")
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
            // Decode URI if needed
            try {
              filename = decodeURIComponent(filename);
            } catch (e) {
              // If decoding fails, use as is
            }
          }
        }
        
        // Fallback to path if no filename found
        if (!filename) {
          filename = path.includes('.') ? path : `${path}.jpg`;
        }
        
        return {
          blob: response.body!,
          filename: filename
        };
      })
    );
  }

  uploadFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.baseUrl}/api/file/upload`;

    return this.http.post(url, formData);
  }

  submitSupport(payload: {
    program: string;
    title: string;
    category: string;
    subCategory: string;
    description: string;
    attachment: string[];
  }): Observable<any> {
    const url = `${this.baseUrl}/api/auth/support`;
    return this.http.post(url, payload);
  }

  // Service endpoints
  getServiceList(): Observable<any> {
    const url = `${this.baseUrl}/api/program/service/list`;
    return this.http.get(url);
  }

  getServiceVendors(serviceId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/service/vendors?service_id=${serviceId}`;
    return this.http.get(url);
  }

  // Get program user data
  getProgramUser(programId: string | number, applicationId?: string | number): Observable<any> {
    const appParam = applicationId != null && String(applicationId).trim() !== ''
      ? `&application_id=${encodeURIComponent(String(applicationId))}`
      : '';
    const url = `${this.baseUrl}/api/program/user?program_id=${programId}${appParam}`;
    return this.http.get(url);
  }

  // Get program data by user ID (new API)
  getProgramDataByUserId(userId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/user/programdata?user_id=${userId}`;
    return this.http.get(url);
  }

  // Get family members for the authenticated user (profile)
  getFamilyMembers(includeInactive = false): Observable<any> {
    const inactiveParam = includeInactive ? '?include_inactive=true' : '';
    const url = `${this.baseUrl}/api/program/familymembers${inactiveParam}`;
    return this.http.get(url);
  }

  // Get family members by user ID (program flows)
  getFamilyMembersByUserId(userId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/familymembers?user_id=${userId}`;
    return this.http.get(url);
  }

  addFamilyMember(payload: Record<string, unknown>): Observable<any> {
    const url = `${this.baseUrl}/api/program/familymembers`;
    return this.http.post(url, payload);
  }

  updateFamilyMember(memberId: string | number, payload: Record<string, unknown>): Observable<any> {
    const url = `${this.baseUrl}/api/program/familymembers/${memberId}`;
    return this.http.put(url, payload);
  }

  deleteFamilyMember(memberId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/familymembers/${memberId}`;
    return this.http.delete(url);
  }

  // Get draft-only application data (excludes submitted/pending rows for the same program)
  getProgramDraft(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/draft?program_id=${programId}`;
    return this.http.get(url);
  }

  /** Cancel/delete in-progress draft for a program (idempotent). */
  deleteProgramDraft(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/draft?program_id=${programId}`;
    return this.http.delete(url);
  }

  getProgramSubmissions(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/submissions?program_id=${programId}`;
    return this.http.get(url);
  }

  getJobStatus(jobId: string): Observable<any> {
    const url = `${this.baseUrl}/api/job/status/${jobId}`;
    return this.http.get(url);
  }

  checkIdentificationNumber(identificationNumber: string): Observable<any> {
    const url = `${this.baseUrl}/api/auth/check-identification-number`;
    return this.http.post(url, { identificationNumber });
  }

  // Get program count and application status
  getProgramCount(programId: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/program/count?program_id=${programId}`;
    return this.http.get(url);
  }

}
